import React from 'react';
import { Link } from 'react-router-dom';
import { useDbtProjectOverview, useFailedTests } from '../../hooks/useDbtData';
import DbtMetricsCard from './DbtMetricsCard';
import LoadingSpinner from '../common/LoadingSpinner';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { dbtConfig } from '../../config/dbt';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * DbtProjectDashboard Component
 * Main dashboard showing project overview, metrics, and charts
 */
const DbtProjectDashboard = ({ projectId }) => {
  const { data: overview, isLoading, error } = useDbtProjectOverview(projectId);
  const { data: failedTests } = useFailedTests(projectId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Error loading project overview: {error.message}</span>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="alert alert-warning">
        <span>No overview data available for this project.</span>
      </div>
    );
  }

  // Prepare chart data for models by materialization
  const materializationData = {
    labels: Object.keys(overview.models_by_materialization || {}),
    datasets: [
      {
        data: Object.values(overview.models_by_materialization || {}),
        backgroundColor: [
          '#3b82f6', // blue
          '#10b981', // green
          '#f59e0b', // amber
          '#8b5cf6', // purple
          '#ec4899'  // pink
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  // Prepare chart data for tests by type
  const testTypeData = {
    labels: Object.keys(overview.tests_by_type || {}),
    datasets: [
      {
        label: 'Number of Tests',
        data: Object.values(overview.tests_by_type || {}),
        backgroundColor: '#3b82f6',
        borderColor: '#2563eb',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom'
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <DbtMetricsCard
          title="Total Models"
          value={overview.total_models || 0}
          trend="+5 this week"
          icon={() => (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
            </svg>
          )}
        />

        <DbtMetricsCard
          title="Total Tests"
          value={overview.total_tests || 0}
          trend="+12 this week"
          icon={() => (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        />

        <DbtMetricsCard
          title="Test Success Rate"
          value={`${Math.round(overview.test_success_rate || 0)}%`}
          trend={overview.test_success_rate >= 95 ? (
            <span className="flex items-center"><CheckCircleIcon className="h-3 w-3 mr-1" />Healthy</span>
          ) : (
            <span className="flex items-center"><ExclamationTriangleIcon className="h-3 w-3 mr-1" />Needs attention</span>
          )}
          icon={() => (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          )}
        />

        <DbtMetricsCard
          title="Failed Tests"
          value={overview.failed_tests_count || 0}
          trend={overview.failed_tests_count === 0 ? (
            <span className="flex items-center"><CheckCircleIcon className="h-3 w-3 mr-1" />All passing</span>
          ) : (
            <span className="flex items-center"><ExclamationTriangleIcon className="h-3 w-3 mr-1" />Investigate</span>
          )}
          icon={() => (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Models by Materialization */}
        <div className="bg-white shadow-xl rounded-lg">
          <div className="p-6">
            <h3 className="card-title">Models by Materialization</h3>
            <div className="flex justify-center py-4">
              {Object.keys(overview.models_by_materialization || {}).length > 0 ? (
                <div className="w-64 h-64">
                  <Doughnut data={materializationData} options={chartOptions} />
                </div>
              ) : (
                <p className="text-base-content/60">No model data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Tests by Type */}
        <div className="bg-white shadow-xl rounded-lg">
          <div className="p-6">
            <h3 className="card-title">Tests by Type</h3>
            <div className="py-4">
              {Object.keys(overview.tests_by_type || {}).length > 0 ? (
                <Bar data={testTypeData} options={chartOptions} />
              ) : (
                <p className="text-base-content/60 text-center">No test data available</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Latest Run Status */}
      {overview.latest_run_status && (
        <div className="bg-white shadow-xl rounded-lg">
          <div className="p-6">
            <h3 className="card-title">Latest Run</h3>
            <div className="flex items-center gap-4">
              <span className={`badge ${dbtConfig.statusBadgeVariants[overview.latest_run_status]}`}>
                {overview.latest_run_status}
              </span>
              {overview.latest_run_date && (
                <span className="text-sm text-base-content/70">
                  {new Date(overview.latest_run_date).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Failed Tests Table */}
      {failedTests && failedTests.length > 0 && (
        <div className="bg-white shadow-xl rounded-lg">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="card-title">Recent Test Failures</h3>
              <Link to={`/dbt/projects/${projectId}/tests?status=failed`} className="btn btn-sm btn-ghost">
                View All
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Test Name</th>
                    <th>Status</th>
                    <th>Failures</th>
                    <th>Completed At</th>
                  </tr>
                </thead>
                <tbody>
                  {failedTests.slice(0, 5).map((test, index) => (
                    <tr key={index}>
                      <td>{test.dbt_tests?.name || test.unique_id}</td>
                      <td>
                        <span className={`badge ${dbtConfig.statusBadgeVariants[test.status]}`}>
                          {test.status}
                        </span>
                      </td>
                      <td>{test.failures || 0}</td>
                      <td className="text-sm text-base-content/70">
                        {test.completed_at ? new Date(test.completed_at).toLocaleString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DbtProjectDashboard;
